import { z } from 'zod';

import { phone } from './customer-form-schema';

const LENGTH = 'customers:validation.length';
const optional = (max: number) => z.string().trim().max(max, { error: LENGTH });
const required = (max: number) => optional(max).min(1, { error: 'customers:validation.required' });

/**
 * Countries whose addresses the backend stores with an ISO region code (lib/regions.ts); it
 * refuses a blank region for them with 422 on `body.region`.
 */
export const REGION_COUNTRIES: ReadonlySet<string> = new Set([
  'US',
  'CA',
  'AU',
  'GB',
  'IN',
  'MX',
  'BR',
  'IT',
  'ES',
  'JP',
]);

/** Mirrors the backend's CreateAddressBody limits; blank optional strings go out as null. */
export const customerAddressSchema = z
  .object({
    label: optional(60),
    firstName: optional(120),
    lastName: optional(120),
    company: optional(120),
    line1: required(200),
    line2: optional(200),
    city: required(120),
    region: optional(120),
    postalCode: optional(20),
    countryCode: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}$/, { error: 'customers:validation.country' }),
    phone,
    isDefaultShipping: z.boolean(),
    isDefaultBilling: z.boolean(),
  })
  .refine((value) => !REGION_COUNTRIES.has(value.countryCode) || value.region !== '', {
    path: ['region'],
    error: 'customers:validation.regionRequired',
  });

export type CustomerAddressFormInput = z.input<typeof customerAddressSchema>;
export type CustomerAddressFormOutput = z.output<typeof customerAddressSchema>;

export const ADDRESS_TEXT_FIELDS = [
  'label',
  'firstName',
  'lastName',
  'company',
  'line1',
  'line2',
  'city',
  'region',
  'postalCode',
  'countryCode',
  'phone',
] as const satisfies ReadonlyArray<keyof CustomerAddressFormInput>;

export const ADDRESS_KNOWN_FIELDS: ReadonlySet<string> = new Set([
  ...ADDRESS_TEXT_FIELDS,
  'isDefaultShipping',
  'isDefaultBilling',
]);
